"use client"

import type { PointHoverTooltipPayload } from "./BandHudOverlayChannel"

// Visual config for the point-on-line hover tooltip. Edit these to tweak the
// tooltip box's background, shadow, and the hovered-point dot. Kept here next
// to the render below so the tooltip's look stays one cmd-click away.
const HUD_TOOLTIP_BACKGROUND_COLOR = "var(--playbook-epilogue-bg, #fffff8)"
const HUD_TOOLTIP_BOX_SHADOW =
  "1px 1px 300px 0px rgba(10, 10, 0, 0.1), 1px 1px 3px 0px rgba(0, 0, 0, 0.2)"
const HOVER_POINT_DOT_COLOR = "#000000"
const HOVER_POINT_DOT_SIZE_PX = 5

// The guideTedDash treatment (the matched-guides tab): the guide line wears
// the TED guide's ORIGINAL look — 1px wide, 2px dashes with 3px gaps, full
// strength — in the point hover's own ink. The gradient paints the dash
// rhythm on a 1px-wide div (CSS borders can't do a 2-3 dash).
const POINT_GUIDE_TED_DASH_WIDTH_PX = 1
const POINT_GUIDE_TED_DASH_ON_PX = 2
const POINT_GUIDE_TED_DASH_OFF_PX = 3

// The point-on-line hover tooltip: a vertical dotted guide line, the hovered
// point dot, and the info box ("…RESEARCHER / 29x speedup to AI R&D"). Rendered
// by AboveGlobeBandHudOverlay so it paints above the globe; positions are in
// graph-local coordinates (the layer is pinned to the graph's box), so they
// map straight to left/top. hideInfoBox drops the info box but keeps the dot
// and guide line — used when a CapabilityHoverSwapTarget shows the hovered
// point's info in-canvas instead.
export const GraphPointHoverTooltip = ({
  point,
  hideInfoBox = false,
}: {
  point: PointHoverTooltipPayload
  hideInfoBox?: boolean
}) => (
  <>
    {point.guideLineHeight > 0 && (
      <div
        className="absolute pointer-events-none"
        style={{
          left: point.cx,
          top: point.guideLineTop,
          height: point.guideLineHeight,
          ...(point.guideTedDash
            ? {
                width: POINT_GUIDE_TED_DASH_WIDTH_PX,
                background: `repeating-linear-gradient(to bottom, ${point.textColor} 0 ${POINT_GUIDE_TED_DASH_ON_PX}px, transparent ${POINT_GUIDE_TED_DASH_ON_PX}px ${POINT_GUIDE_TED_DASH_ON_PX + POINT_GUIDE_TED_DASH_OFF_PX}px)`,
              }
            : {
                width: 0,
                borderLeft: `1px dotted ${point.textColor}`,
                opacity: 0.35,
              }),
        }}
      />
    )}
    {!point.dotHidden && (
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          left: point.cx,
          top: point.cy,
          width: HOVER_POINT_DOT_SIZE_PX,
          height: HOVER_POINT_DOT_SIZE_PX,
          transform: "translate(-50%, -50%)",
          backgroundColor: HOVER_POINT_DOT_COLOR,
        }}
      />
    )}
    {!hideInfoBox && <div
      className="absolute text-black px-2 py-2 rounded-sm text-sm font-sans whitespace-nowrap pointer-events-none flex flex-col gap-1"
      style={{
        left: point.cx,
        top: point.cy - 7,
        transform: "translate(calc(-100% - 6px), -100%)",
        backgroundColor: HUD_TOOLTIP_BACKGROUND_COLOR,
        boxShadow: HUD_TOOLTIP_BOX_SHADOW,
      }}
    >
      <div className="font-mono -mx-2 px-2 border-b border-black/10 pb-1 text-[12px] font-semibold tracking-wide uppercase">
        {point.capabilityThresholdLabel}
      </div>
      <div className="text-sm">{point.speedupText} speedup to AI R&amp;D.</div>
      {point.riskLabel && (
        <div className="opacity-50 text-[11px]">{point.riskLabel}</div>
      )}
      {point.detailsHint && (
        <div className="-mx-2 mt-0.5 border-t border-black/10 px-2 pt-1.5 text-[10px] text-[var(--race-background)]">
          {point.detailsHint}
        </div>
      )}
    </div>}
  </>
)
